import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["idea", "filming", "editing", "ready", "posted", "archived"];

export default function Library({ extraTabBar }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", platform: "", status: "idea", notes: "", scheduled_for: "", thumbnail: "" });

  const load = async () => setItems((await api.get("/api/content")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title: "", platform: "", status: "idea", notes: "", scheduled_for: "", thumbnail: "" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ title: it.title, platform: it.platform || "", status: it.status, notes: it.notes || "", scheduled_for: it.scheduled_for || "", thumbnail: it.thumbnail || "" }); setOpen(true); };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (editing) await api.put(`/api/content/${editing.id}`, form);
    else await api.post("/api/content", form);
    setOpen(false); load();
  };
  const remove = async (id) => { await api.delete(`/api/content/${id}`); load(); };

  const seedPipeline = async () => {
    try {
      const r = await api.post("/api/seed/pipeline");
      toast.success(`Seeded ${r.data.seeded} videos from your edit pipeline 🎬`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const groups = STATUSES.map((s) => ({ s, list: items.filter((i) => i.status === s) }));

  return (
    <AppLayout title="Content library" overline="070 · IDEA → POSTED">
      {extraTabBar}
      <div className="flex justify-end gap-3 mb-4">
        {items.length === 0 && (
          <button onClick={seedPipeline} data-testid="library-seed-button"
            className="btn-ghost inline-flex items-center gap-2">
            <Sparkles size={16} /> Seed edit pipeline
          </button>
        )}
        <button onClick={openNew} data-testid="library-new-button" className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New piece
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4" data-testid="library-board">
        {groups.map(({ s, list }) => (
          <div key={s} className="border border-zinc-900 bg-zinc-950 p-4 min-h-[240px]" data-testid={`col-${s}`}>
            <div className="overline mb-4 text-[color:var(--gio-primary)]">{s} · {list.length}</div>
            <div className="space-y-3">
              {list.map((it) => (
                <div key={it.id} className="group border border-zinc-800 p-3 hover:border-[color:var(--gio-primary)] transition" data-testid={`content-${it.id}`}>
                  {it.thumbnail && <img src={it.thumbnail} alt="" className="w-full aspect-video object-cover mb-2" />}
                  <div className="flex justify-between gap-2">
                    <div className="text-white text-sm font-medium">{it.title}</div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => openEdit(it)} className="text-zinc-500 hover:text-[color:var(--gio-primary)]"><Pencil size={14} /></button>
                      <button onClick={() => remove(it.id)} className="text-zinc-500 hover:text-[color:var(--gio-primary)]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  {it.platform && <div className="overline mt-1">{it.platform}</div>}
                  {it.notes && <div className="text-zinc-500 text-xs mt-2">{it.notes}</div>}
                </div>
              ))}
              {list.length === 0 && <div className="text-zinc-700 text-xs">—</div>}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="library-modal">
          <div className="bg-zinc-950 border border-zinc-800 max-w-lg w-full p-6">
            <div className="overline mb-4 text-[color:var(--gio-primary)]">{editing ? "EDIT" : "NEW"} · CONTENT PIECE</div>
            <F l="Title"><input data-testid="lib-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="gio-input" /></F>
            <F l="Status">
              <select data-testid="lib-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="gio-input">
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </F>
            <F l="Platform"><input data-testid="lib-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} placeholder="instagram, tiktok..." className="gio-input" /></F>
            <F l="Scheduled for"><input type="date" data-testid="lib-schedule" value={form.scheduled_for || ""} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} className="gio-input" /></F>
            <F l="Thumbnail URL"><input data-testid="lib-thumb" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} placeholder="https://..." className="gio-input" /></F>
            <F l="Notes"><textarea data-testid="lib-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="gio-input" /></F>
            <div className="flex gap-3 mt-4">
              <button onClick={save} className="btn-primary flex-1" data-testid="lib-save-button">Save</button>
              <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="lib-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.gio-input { width: 100%; background: #000; border: 1px solid #27272a; color: white; padding: 10px; font-size: 13px; outline: none; } .gio-input:focus { border-color: var(--gio-primary); }`}</style>
    </AppLayout>
  );
}

function F({ l, children }) { return <div className="mb-3"><div className="overline mb-2">{l}</div>{children}</div>; }
